import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils";
import { Field } from "../ui/Field";

export interface LocationOption {
  readonly id: string;
  readonly name: string;
  readonly orgName?: string | null;
}

export interface LocationPickerProps {
  readonly options: readonly LocationOption[];
  readonly value: string | null;
  readonly onChange: (id: string) => void;
  readonly disabled?: boolean;
}

/** Location picker - only rendered when the active membership is
 *  NOT location-scoped (e.g. partner_admin / org_admin doing a
 *  hand-on ring-up). Shows the location's name with the org name
 *  as a sublabel for disambiguation. */
export function LocationPicker({ options, value, onChange, disabled }: LocationPickerProps) {
  const { t } = useTranslation();

  return (
    <Field label={t("sale.location")}>
      <div className="relative">
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={cn(
            "block w-full appearance-none rounded-md bg-surface py-2 pl-3 pr-9 text-sm text-strong",
            "ring-1 ring-inset ring-line transition-shadow",
            "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-primary",
            "disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-muted",
          )}
        >
          {options.length === 0 && <option value="">{t("sale.noLocationsAvailable")}</option>}
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
              {opt.orgName ? ` - ${opt.orgName}` : ""}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint"
          aria-hidden="true"
        />
      </div>
    </Field>
  );
}
